/**
 * Routing helpers - mapping miedzy React Router URL path a custom route object
 * z new-design (mock router uzywal useState({name, toolId, projectId, tab, filter})).
 *
 * URL conventions:
 *   /                       -> {name: 'hub'}
 *   /studio/:toolSlug       -> {name: 'tool', toolId: toolSlug}
 *   /wyceny[/:id]           -> {name: 'valuations'} (lista zapisanych wycen i raport)
 *   /oferty                 -> {name: 'gallery'} (lista ofert)
 *   /oferty/:projId         -> {name: 'project', projectId}
 *   /oferta/nowa|:projId    -> {name: 'offer', projectId} (kreator)
 *   /galeria                -> {name: 'photos'} (galeria zdjęć, z ?filter=unassigned)
 *   /marka                  -> {name: 'brand', tab: ?tab=logo}
 *   /agencja                -> {name: 'agency', tab: ?tab=zuzycie}
 *   /konto                  -> {name: 'account', tab: ?tab=dane}
 *   /cennik                 -> {name: 'pricing'} (paczki kredytów — stary cennik)
 *   /cennik-abonament       -> {name: 'plans'}   (plany abonamentowe)
 */
import type { NavRoute, RouteName } from "@/lib/data";

export function routeToUrl(route: NavRoute): string {
  switch (route.name) {
    case "hub":
      return "/";
    case "tool":
      return route.toolId ? `/studio/${route.toolId}` : "/";
    case "valuations":
      return "/wyceny";
    case "gallery":
      return route.filter ? `/oferty?filter=${route.filter}` : "/oferty";
    case "project":
      return `/oferty/${route.projectId ?? ""}`;
    case "offer":
      return route.projectId ? `/oferta/${route.projectId}` : "/oferta/nowa";
    case "photos":
      return route.filter ? `/galeria?filter=${route.filter}` : "/galeria";
    case "brand":
      return route.tab ? `/marka?tab=${route.tab}` : "/marka";
    case "agency":
      return route.tab ? `/agencja?tab=${route.tab}` : "/agencja";
    case "account":
      return route.tab ? `/konto?tab=${route.tab}` : "/konto";
    case "pricing":
      return "/cennik";
    case "plans":
      return "/cennik-abonament";
    case "admin":
      return route.tab ? `/admin?tab=${route.tab}` : "/admin";
  }
}

/**
 * Match URL path -> route name (do detekcji active state w nav).
 * Path checks bez query string.
 */
export function urlToRouteName(pathname: string): RouteName | null {
  if (pathname === "/") return "hub";
  if (pathname.startsWith("/studio/")) return "tool";
  // Szczegół wyceny celowo mapuje się na tę samą nazwę co lista — nazwa steruje
  // wyłącznie podświetleniem menu, a „Moje wyceny" ma zostać aktywne w raporcie.
  if (pathname.startsWith("/wyceny")) return "valuations";
  if (pathname.startsWith("/oferta")) return "offer";
  if (pathname === "/oferty" || pathname === "/oferty/") return "gallery";
  if (pathname.startsWith("/oferty/")) return "project";
  if (pathname.startsWith("/galeria/projekt/")) return "project"; // legacy → redirect na /oferty/:id
  if (pathname.startsWith("/galeria")) return "photos";
  if (pathname.startsWith("/marka")) return "brand";
  if (pathname.startsWith("/agencja")) return "agency";
  if (pathname.startsWith("/konto")) return "account";
  if (pathname === "/cennik") return "pricing";
  if (pathname === "/cennik-abonament") return "plans";
  if (pathname.startsWith("/admin")) return "admin";
  return null;
}

/**
 * Zakładka domyślna dla stron, na których brak `?tab=` znaczy konkretny widok
 * (np. /agencja bez parametru = Zespół). Bez tego pozycja menu „Zespół" (target
 * bez `tab`) pasowałaby też do /agencja?tab=zuzycie.
 */
const DEFAULT_TAB: Partial<Record<RouteName, string>> = {
  brand: "logo",
  agency: "team",
  admin: "stats",
};

/**
 * routeMatches - port z new-design/layout.jsx, z symetrycznym porównaniem tab/filter.
 *
 * Uwaga (2026-07-21): pierwotna wersja porównywała `tab`/`filter` tylko gdy miał je
 * target — więc pozycja bez filtra („Wszystkie oferty") podświetlała się także na
 * /oferty?filter=archived, razem z „Archiwum". Teraz obie strony są normalizowane
 * i porównywane wprost, więc w danej sekcji aktywna jest dokładnie jedna pozycja.
 */
export function routeMatches(route: NavRoute, target: NavRoute): boolean {
  if (route.name !== target.name) return false;
  if (target.toolId && route.toolId !== target.toolId) return false;
  if (target.projectId && route.projectId !== target.projectId) return false;
  const defTab = DEFAULT_TAB[target.name] ?? null;
  const rTab = route.tab ?? defTab;
  const tTab = target.tab ?? defTab;
  if (target.name === "admin" && !target.tab && ["biznes", "stats", "analytics", "checkouts", "copilot"].includes(rTab ?? "")) {
    return true;
  }
  if (rTab !== tTab) return false;
  if ((route.filter ?? null) !== (target.filter ?? null)) return false;
  return true;
}
